<script lang="ts" setup>
import { ref, onMounted, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useDebounceFn } from '@vueuse/core';
import useCartStore from '../../../store';

const { cardHolderName, cardFieldError } = storeToRefs(useCartStore());

const creditCardModel = defineModel();
const emits = defineEmits<{
  onStripeCardValidated: [];
}>();

defineProps<{
  creditCardErrorMessage: {
    name: string;
    creditCard: string;
  };
}>();

let cardElement: any;
let elements: any;
let stripe: any;
let stripePromise: Promise<any> | null = null;

const showProgressBar = ref(false);
const _token = ref();

watch(cardHolderName, () => onNewCardHolderName());

onMounted(() => setupStripe());

function onNewCardHolderName() {
  creditCardModel.value = {
    name: cardHolderName.value,
    token: _token.value,
  };
}

async function setupStripe() {
  if (!stripePromise) {
    const { loadStripe } = await import('@stripe/stripe-js');
    stripePromise = loadStripe(import.meta.env.VITE_STRIPE_KEY);
  }

  stripe = await stripePromise;
  elements = stripe.elements();
  cardElement = elements.create('card', { style: { base: { fontSize: '14px' } } });

  const debouncedValidateCard = useDebounceFn(() => {
    onValidateCard();
  }, 500);

  cardElement.on('change', () => debouncedValidateCard());

  cardElement.mount('#card-element');
}

async function onValidateCard() {
  showProgressBar.value = true;
  cardFieldError.value = '';

  const { token, error: stripeError } = await stripe.createToken(cardElement, {
    name: cardHolderName.value,
  });

  _token.value = token;

  if (stripeError) {
    cardFieldError.value = stripeError.message;
    showProgressBar.value = false;
    return;
  }

  creditCardModel.value = {
    name: cardHolderName.value,
    token: _token.value,
  };

  showProgressBar.value = false;
  emits('onStripeCardValidated');
}
</script>

<template>
  <div class="flex flex-col gap-6">
    <div class="relative flex w-full flex-col">
      <AFieldLabel
        required
        :error="creditCardErrorMessage?.name"
        :label="$t('common.name')"
      />

      <AFieldInputText
        required
        v-model="cardHolderName"
        :placeholder="$t('cart.paymentMethod.validateName')"
      />

      <AFieldError
        class="absolute bottom-[-24px]"
        :error="creditCardErrorMessage?.name"
      />
    </div>

    <div class="relative flex w-full flex-col">
      <AFieldLabel
        required
        :error="cardFieldError || creditCardErrorMessage?.creditCard"
        :label="$t('cart.label.creditCard')"
      />

      <div
        class="rounded-md border border-slate-300 px-3 py-2"
        id="card-element"
      ></div>

      <AFieldError
        v-if="!showProgressBar"
        class="absolute bottom-[-4px]"
        :error="cardFieldError || creditCardErrorMessage?.creditCard"
      />

      <div class="flex h-5 items-center gap-2">
        <AFieldLabel
          v-if="showProgressBar"
          class="text-xs text-slate-500"
          :label="$t('cart.paymentMethod.validating')"
        />

        <ProgressBar
          v-if="showProgressBar"
          class="h-0.5 w-full"
          mode="indeterminate"
          pt:value="bg-brand-400"
        />
      </div>
    </div>
  </div>
</template>
